import { useSession } from "@d12labs/auth/client";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageHeader } from "@d12labs/ui/page-header";
import { cn, formatClock } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
	BarChart2,
	Download,
	FileText,
	Maximize2,
	Minimize2,
	Presentation,
	RefreshCw,
	Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";
import { parseActionItems } from "../components/action-items";
import { SectionStream } from "../components/analyze-pane";
import { DimensionRail } from "../components/dimension-rail";
import { MeetingAnalyticsStrip } from "../components/meeting-analytics";
import { MeetingDateEditor } from "../components/meeting-date-editor";
import { RecordingPanel } from "../components/recording-panel";
import { TranscriptPane } from "../components/transcript-pane";
import { Waveform } from "../components/waveform";
import { useMeetingDetail } from "../hooks/use-meeting-detail";
import {
	type Analysis,
	CATEGORY_KEYS,
	type CategoryKey,
	type MeetingDetail,
	meetingTypeLabel,
} from "../types";

/** Pull the six 0–10 category scores out of an analysis as a flat record. */
const categoryScores = (analysis: Analysis): Record<CategoryKey, number> =>
	Object.fromEntries(
		CATEGORY_KEYS.map((k) => [k, analysis.categoryScores[k]?.score ?? 0]),
	) as Record<CategoryKey, number>;

function formatDuration(min: number | null, sec: number | null): string {
	if (sec != null) return formatClock(sec);
	if (min != null) return `${min} min`;
	return "—";
}

function weekNumber(date: Date): number {
	const start = new Date(date.getFullYear(), 0, 1);
	return Math.ceil(((date.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
}

export function MeetingDetailPage() {
	const { id } = useParams({ strict: false }) as { id: string };
	const qc = useQueryClient();
	const navigate = useNavigate();

	// Track in-flight analysis per speaker
	const [analyzing, setAnalyzing] = useState<Set<string>>(new Set());
	const [fetchKicked, setFetchKicked] = useState(false);

	const { data, isLoading, error } = useQuery({
		queryKey: ["meeting-perf", "meeting", id],
		// The analysis jsonb columns are loosely typed on the wire row; the route's
		// enrichment guarantees the narrowed `Analysis` fields at runtime (../types).
		queryFn: () => meetingPerfApi.meetingDetail({ params: { id } }) as Promise<MeetingDetail>,
		refetchInterval: (query) => {
			const d = query.state.data;
			const recordingPending = d?.recordings?.some((r) => r.status === "pending") ?? false;
			return analyzing.size > 0 ||
				(d?.pendingSpeakerIds?.length ?? 0) > 0 ||
				recordingPending ||
				fetchKicked
				? 2500
				: false;
		},
	});

	// Latest analysis per speaker (listByMeeting returns newest-first)
	const analysisBySpeaker = new Map<string, Analysis>();
	for (const a of data?.analyses ?? []) {
		if (!analysisBySpeaker.has(a.speakerId)) analysisBySpeaker.set(a.speakerId, a);
	}

	// Clear the "analyzing" flag once a speaker's analysis lands
	useEffect(() => {
		if (analyzing.size === 0) return;
		setAnalyzing((prev) => {
			let changed = false;
			const next = new Set(prev);
			for (const spId of prev) {
				if (analysisBySpeaker.has(spId)) {
					next.delete(spId);
					changed = true;
				}
			}
			if (changed) toast.success("Analysis ready");
			return changed ? next : prev;
		});
	}, [analyzing, analysisBySpeaker]);

	const analyze = useMutation({
		mutationFn: (speakerId: string) =>
			meetingPerfApi.analyzeMeeting({ params: { id }, input: { speakerId } }),
		onSuccess: (_res, speakerId) => {
			setAnalyzing((prev) => new Set(prev).add(speakerId));
			toast.info("Analysis queued");
		},
		onError: (err) => toast.error((err as Error).message),
	});

	const invalidateMeeting = () =>
		qc.invalidateQueries({ queryKey: ["meeting-perf", "meeting", id] });

	const fetchMedia = useMutation({
		mutationFn: () => meetingPerfApi.fetchMedia({ params: { id } }),
		onSuccess: () => {
			toast.info("Fetching recording…");
			setFetchKicked(true);
			invalidateMeeting();
		},
		onError: (err) => toast.error((err as Error).message),
	});

	useEffect(() => {
		if (fetchKicked && data?.recordings.some((r) => r.status === "pending")) setFetchKicked(false);
	}, [fetchKicked, data?.recordings]);

	useEffect(() => {
		if (!fetchKicked) return;
		const timer = setTimeout(() => setFetchKicked(false), 12_000);
		return () => clearTimeout(timer);
	}, [fetchKicked]);

	const deleteMeeting = useMutation({
		mutationFn: () => meetingPerfApi.removeMeeting({ params: { id } }),
		onSuccess: () => {
			toast.success("Meeting deleted");
			qc.invalidateQueries({ queryKey: ["meeting-perf"] });
			navigate({ to: "/meeting-perf/meetings" });
		},
		onError: (err) => toast.error((err as Error).message),
	});

	// Speaker ordering: encounter order from turns, then remaining
	const speakerOrder: string[] = [];
	for (const turn of data?.turns ?? [])
		if (!speakerOrder.includes(turn.speakerId)) speakerOrder.push(turn.speakerId);
	for (const s of data?.speakers ?? []) {
		const sid = s.meetingSpeaker.speakerId;
		if (!speakerOrder.includes(sid)) speakerOrder.push(sid);
	}

	// speakerMeta map: id → { name, colorIndex }
	const speakerNameById = new Map(
		(data?.speakers ?? []).map((s) => [s.meetingSpeaker.speakerId, s.speaker?.name ?? "Speaker"]),
	);
	const speakerMetaMap = new Map(
		speakerOrder.map((sid, i) => [
			sid,
			{ name: speakerNameById.get(sid) ?? "Speaker", colorIndex: i },
		]),
	);

	const durationHint =
		data?.recordings.find((r) => r.durationSec != null)?.durationSec ??
		(data?.meeting.durationMinutes != null ? data.meeting.durationMinutes * 60 : null);

	// Default first speaker for the hook
	const defaultSpeakerId = speakerOrder[0] ?? "";

	// Default to null so the page opens on the sections stream (stream-first entry point)
	const defaultDim: CategoryKey | null = null;

	const {
		view,
		setView,
		dim,
		setDim,
		section,
		setSection,
		speakerId,
		setSpeakerId,
		playerOpen,
		setPlayerOpen,
		playhead,
		setPlayhead,
		setSpeakers,
	} = useMeetingDetail(defaultSpeakerId, defaultDim);

	// Keep speaker list in sync for keyboard Up/Down.
	// biome-ignore lint/correctness/useExhaustiveDependencies: speakerOrder is a stable primitive join — array identity changes every render
	useEffect(() => {
		setSpeakers(speakerOrder);
	}, [speakerOrder.join(","), setSpeakers]);

	// On first render the detail query hasn't resolved yet, so the hook captures
	// an empty `speakerId`. Push the resolved default in once the data lands so
	// the Analyze pane and rail agree on which speaker is active.
	useEffect(() => {
		if (!speakerId && defaultSpeakerId) setSpeakerId(defaultSpeakerId);
	}, [speakerId, defaultSpeakerId, setSpeakerId]);

	// Audio is owned here so quote/annotation clicks in the Analyze pane can seek
	// even when the Recording panel is collapsed. Video lives inside the panel
	// (visible <video controls>) so it "opens up" with native scrub + fullscreen.
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const { data: sessionData } = useSession();

	// Wire the canonical playhead + play state from the audio element. Re-attaches
	// when recordings change (the <audio>/<video> elements mount once recs exist).
	// biome-ignore lint/correctness/useExhaustiveDependencies: data?.recordings is the element-mounts re-attach key
	useEffect(() => {
		const a = audioRef.current;
		if (!a) return;
		const onPlay = () => setIsPlaying(true);
		const onPause = () => setIsPlaying(false);
		const onTime = () => {
			if (a.duration > 0) setPlayhead(a.currentTime / a.duration);
		};
		a.addEventListener("play", onPlay);
		a.addEventListener("pause", onPause);
		a.addEventListener("ended", onPause);
		a.addEventListener("timeupdate", onTime);
		return () => {
			a.removeEventListener("play", onPlay);
			a.removeEventListener("pause", onPause);
			a.removeEventListener("ended", onPause);
			a.removeEventListener("timeupdate", onTime);
		};
	}, [data?.recordings, setPlayhead]);

	// ── Loading / error states ──────────────────────────────────────────────
	if (isLoading) {
		return (
			<>
				<PageHeader eyebrow="Meeting Performance" title="Loading…" icon={Presentation} />
				<div className="h-[2px] bg-primary animate-pulse" />
			</>
		);
	}
	if (error || !data) {
		return (
			<>
				<PageHeader eyebrow="Meeting Performance" title="Not found" icon={Presentation} />
				<div className="flex flex-1 items-center justify-center">
					<p className="text-body-sm text-destructive">Meeting not found.</p>
				</div>
			</>
		);
	}

	const { meeting } = data;
	const serverPending = new Set(data.pendingSpeakerIds);

	// ── Computed header values ──────────────────────────────────────────────
	const meetingDate = new Date(meeting.meetingDate);
	const weekNum = weekNumber(meetingDate);
	const eyebrow = `MEETING PERFORMANCE / WEEK ${weekNum}`;

	const scored = [...analysisBySpeaker.values()].map((a) => Number(a.overallScore));
	const meetingAvg = scored.length
		? Math.round(scored.reduce((s, v) => s + v, 0) / scored.length)
		: null;

	// The date leads the subtitle and is editable in place (MeetingDateEditor).
	const subtitleParts: string[] = [];
	if (durationHint != null) {
		subtitleParts.push(formatDuration(null, durationHint));
	} else if (meeting.durationMinutes) {
		subtitleParts.push(`${meeting.durationMinutes} min`);
	}
	subtitleParts.push(`${meeting.participantCount ?? data.speakers.length} attendees`);
	if (meeting.source === "fireflies") subtitleParts.push("Fireflies");

	// ── Active speaker data ─────────────────────────────────────────────────
	const activeSpeakerEntry = data.speakers.find((s) => s.meetingSpeaker.speakerId === speakerId);
	const activeAnalysis = analysisBySpeaker.get(speakerId);

	// Build the DimensionRail speaker list
	// Preserve the server-provided order (organizer-first, then airtime% desc) —
	// do NOT reorder by the turns-derived `speakerOrder` (that drives the
	// waveform colouring / default active speaker, which intentionally stays
	// in turns-encounter order).
	const railSpeakers = data.speakers.map(({ meetingSpeaker, speaker, isOrganizer }) => {
		const anal = analysisBySpeaker.get(meetingSpeaker.speakerId);
		const scores = anal ? categoryScores(anal) : ({} as Record<CategoryKey, number>);
		const dims = Object.fromEntries(
			CATEGORY_KEYS.map((k) => [k, { score: scores[k] ?? 0 }]),
		) as Record<CategoryKey, { score: number }>;
		return {
			speakerId: meetingSpeaker.speakerId,
			name: speaker?.name ?? "Unknown",
			airtimePct: meetingSpeaker.airtimePct,
			overallScore: anal ? Number(anal.overallScore) : 0,
			dimensions: dims,
			firefliesAnalytics: meetingSpeaker.firefliesAnalytics ?? null,
			isOrganizer,
		};
	});

	// Full per-dim content map for SectionStream
	const dimContentMap = Object.fromEntries(
		CATEGORY_KEYS.map((k) => [
			k,
			activeAnalysis
				? {
						score: activeAnalysis.categoryScores[k]?.score ?? 0,
						analysis:
							activeAnalysis.categoryScores[k]?.analysis ||
							activeAnalysis.categoryScores[k]?.feedback ||
							"",
						wins: activeAnalysis.categoryScores[k]?.wins ?? [],
						changes: activeAnalysis.categoryScores[k]?.changes ?? [],
					}
				: null,
		]),
	) as Record<
		CategoryKey,
		{ score: number; analysis: string; wins: string[]; changes: string[] } | null
	>;

	// Total action-item task count for the sections nav badge
	const actionItemCount = parseActionItems(data.meeting.metadata.actionItems ?? null).reduce(
		(n, g) => n + g.items.length,
		0,
	);

	// Quotes for active speaker + active dimension
	const activeQuotes =
		activeAnalysis && dim !== null
			? (activeAnalysis.notableQuotes ?? []).filter((q) => q.dimensionKey === dim)
			: [];

	// Speaker name map for transcript
	const speakerNames = new Map(
		data.speakers.map((s) => [s.meetingSpeaker.speakerId, s.speaker?.name ?? "Speaker"]),
	);

	// Compact playhead time display
	const audioRec = data.recordings.find((r) => r.kind === "audio" && r.status === "stored");
	const videoRec = data.recordings.find((r) => r.kind === "video" && r.status === "stored");
	// Total seconds of the canonical media (audio leads; fall back to video, then meeting duration).
	const totalSec =
		audioRec?.durationSec ??
		videoRec?.durationSec ??
		(meeting.durationMinutes != null ? meeting.durationMinutes * 60 : null);
	const currentSec = totalSec != null ? playhead * totalSec : 0;
	const playheadLabel =
		totalSec != null
			? `${formatDuration(null, currentSec)} / ${formatDuration(null, totalSec)}`
			: "—";

	const hasAnalyses = data.analyses.length > 0;
	const isAnalyzing = analyzing.has(speakerId) || serverPending.has(speakerId);

	// Active speaker measurements (for chips above SectionStream)
	const activeMsFireflies = activeSpeakerEntry?.meetingSpeaker.firefliesAnalytics ?? null;

	return (
		<>
			{/* ── Page header ─────────────────────────────────────────────────── */}
			<PageHeader
				eyebrow={eyebrow}
				icon={Presentation}
				title={meeting.title}
				subtitle={
					<>
						<MeetingDateEditor meetingId={meeting.id} value={meetingDate} />
						{subtitleParts.length ? ` · ${subtitleParts.join(" · ")}` : null}
					</>
				}
				badges={
					<div className="flex items-center gap-1.5">
						<Badge tone={meeting.metadata.meetingType?.trim() ? "neutral" : "subtle"}>
							{meetingTypeLabel(meeting.metadata.meetingType)}
						</Badge>
						{meetingAvg != null ? (
							<span
								className={cn(
									"inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5",
									meetingAvg >= 70
										? "border-semantic-success/30 bg-semantic-success/10"
										: "border-warning/30 bg-warning/10",
								)}
							>
								<span
									className={cn(
										"text-body-sm font-semibold tabular-nums",
										meetingAvg >= 70 ? "text-semantic-success" : "text-warning",
									)}
								>
									{meetingAvg}
								</span>
								<span className="text-meta text-ink-subtle">/100</span>
							</span>
						) : null}
					</div>
				}
				actions={
					<>
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={() => fetchMedia.mutate()}
							disabled={fetchMedia.isPending || fetchKicked}
							title="Re-fetch recording"
						>
							<RefreshCw className="size-4" />
						</Button>
						<Button variant="ghost" size="icon-sm" title="Export (coming soon)" disabled>
							<Download className="size-4" />
						</Button>
						<ConfirmDialog
							title="Delete meeting?"
							description={
								meeting.source === "fireflies"
									? "This permanently removes the meeting, its analyses, and any stored recordings. It won't be imported from Fireflies again — find it under Import from Fireflies → Removed to restore it."
									: "This permanently removes the meeting, its analyses, and any stored recordings. This cannot be undone."
							}
							confirmLabel="Delete meeting"
							destructive
							onConfirm={() => deleteMeeting.mutate()}
						>
							<Button
								variant="ghost"
								size="icon-sm"
								disabled={deleteMeeting.isPending}
								className="text-ink-subtle hover:text-destructive"
								title="Delete meeting"
							>
								<Trash2 className="size-4" />
							</Button>
						</ConfirmDialog>
					</>
				}
			/>

			{/* ── Sticky toolbar ───────────────────────────────────────────────── */}
			<div className="sticky top-0 z-10 flex h-11 items-center gap-3 border-b border-hairline bg-canvas/95 px-4 backdrop-blur">
				{!playerOpen ? (
					<>
						{/* Play button */}
						<button
							type="button"
							className="flex size-7 flex-none items-center justify-center rounded-full bg-ink text-canvas hover:bg-ink-muted"
							onClick={() => {
								if (audioRef.current) {
									if (audioRef.current.paused) {
										void audioRef.current.play();
									} else {
										audioRef.current.pause();
									}
								}
							}}
							title={isPlaying ? "Pause" : "Play"}
						>
							{isPlaying ? (
								<svg viewBox="0 0 12 12" className="size-3" fill="currentColor" aria-hidden="true">
									<rect x="3" y="2" width="2.5" height="8" rx="0.5" />
									<rect x="6.5" y="2" width="2.5" height="8" rx="0.5" />
								</svg>
							) : (
								<svg
									viewBox="0 0 12 12"
									className="ml-0.5 size-3"
									fill="currentColor"
									aria-hidden="true"
								>
									<path d="M3 1.5v9l8-4.5L3 1.5Z" />
								</svg>
							)}
						</button>

						{/* Time display */}
						<span className="font-mono text-meta text-ink-subtle tabular-nums">
							{playheadLabel}
						</span>

						{/* Mini waveform */}
						<div className="mx-2 flex-1">
							<Waveform height={24} bars={180} compact playhead={playhead} />
						</div>
					</>
				) : (
					<div className="flex-1" />
				)}

				{/* Recording toggle */}
				<button
					type="button"
					onClick={() => setPlayerOpen((v) => !v)}
					className="flex h-7 items-center gap-1.5 rounded-md border border-hairline bg-surface-1 px-2 text-body-sm text-ink-subtle hover:text-ink"
					title={playerOpen ? "Collapse recording" : "Expand recording"}
				>
					{playerOpen ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
					Recording
				</button>

				{/* Vertical hairline */}
				<span className="h-5 w-px bg-hairline" />

				{/* View toggle */}
				<div className="flex h-7 items-center rounded-md border border-hairline bg-surface-1 p-0.5">
					{(
						[
							["analyze", "Analyze", BarChart2],
							["transcript", "Transcript", FileText],
						] as const
					).map(([k, label, Icon]) => (
						<button
							key={k}
							type="button"
							onClick={() => setView(k)}
							className={`flex h-6 items-center gap-1.5 rounded px-2 text-body-sm transition-colors ${
								view === k ? "bg-surface-3 text-ink" : "text-ink-subtle hover:text-ink"
							}`}
						>
							<Icon className="size-3.5" />
							{label}
						</button>
					))}
				</div>
			</div>

			{/* Meeting analytics strip (category counts + informational sentiment) */}
			<MeetingAnalyticsStrip analytics={meeting.metadata.firefliesMeetingAnalytics} />

			{/* ── Recording panel (expandable) ────────────────────────────────── */}
			{playerOpen ? (
				<RecordingPanel
					recordings={data.recordings}
					turns={data.turns}
					speakerMeta={speakerMetaMap}
					playhead={playhead}
					onPlayheadChange={setPlayhead}
					onRefetch={() => fetchMedia.mutate()}
					fetching={fetchMedia.isPending || fetchKicked}
					audioRef={audioRef}
					isPlaying={isPlaying}
				/>
			) : null}

			{/* Hidden audio element for toolbar play */}
			{/* biome-ignore lint/a11y/useMediaCaption: hidden audio for programmatic scrubbing; transcript is the caption */}
			{audioRec?.url ? <audio ref={audioRef} src={audioRec.url} className="hidden" /> : null}

			{/* ── Two-column workspace ─────────────────────────────────────────── */}
			<div className="grid min-h-0 flex-1 grid-cols-[260px_1fr] overflow-hidden">
				<DimensionRail
					speakers={railSpeakers}
					activeSpeakerId={speakerId}
					activeDim={dim}
					view={view}
					onSpeakerChange={setSpeakerId}
					onDimChange={setDim}
					activeSection={section}
					onSectionChange={setSection}
					actionItemCount={actionItemCount}
				/>

				{/* Main pane */}
				{!hasAnalyses ? (
					<div className="flex items-center justify-center overflow-y-auto">
						{isAnalyzing ? (
							<EmptyState
								icon={Presentation}
								title="Analysis in progress"
								body="Scores are being computed now. This usually takes under a minute."
								primary={
									<Button variant="ghost" size="sm" onClick={() => invalidateMeeting()}>
										<RefreshCw className="size-3.5" />
										Refresh
									</Button>
								}
							/>
						) : (
							<EmptyState
								icon={Presentation}
								title="Not analyzed yet"
								body="Run the analysis to see dimension scores, insights, and transcript annotations."
								primary={
									activeSpeakerEntry ? (
										<Button
											size="sm"
											disabled={analyze.isPending}
											onClick={() => analyze.mutate(activeSpeakerEntry.meetingSpeaker.speakerId)}
										>
											Analyze
										</Button>
									) : undefined
								}
							/>
						)}
					</div>
				) : view === "analyze" ? (
					activeAnalysis ? (
						<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
							{/* Model + measurements chips (above SectionStream) */}
							{(() => {
								const fa = activeMsFireflies;
								const chips: string[] = [];
								if (fa?.fillerWords !== null && fa?.fillerWords !== undefined)
									chips.push(`${fa.fillerWords} fillers`);
								if (fa?.longestMonologueSec !== null && fa?.longestMonologueSec !== undefined)
									chips.push(`${Math.round(fa.longestMonologueSec)}s longest monologue`);
								if (fa?.questionsCount !== null && fa?.questionsCount !== undefined)
									chips.push(`${fa.questionsCount} questions`);
								if (fa?.durationPct !== null && fa?.durationPct !== undefined)
									chips.push(`${Math.round(fa.durationPct * 100)}% airtime`);
								return (
									<div className="flex flex-wrap gap-1.5 border-b border-hairline bg-canvas px-8 py-2.5">
										<Badge tone="subtle">Model · {activeAnalysis.modelVersion}</Badge>
										{chips.map((chip) => (
											<Badge key={chip} tone="subtle">
												{chip}
											</Badge>
										))}
									</div>
								);
							})()}
							<SectionStream
								speakerName={activeSpeakerEntry?.speaker?.name ?? "Speaker"}
								activeDim={dim}
								section={section}
								dimContentMap={dimContentMap}
								activeQuotes={activeQuotes}
								audioRef={audioRef}
								onSeeInTranscript={() => setView("transcript")}
								executiveSummary={activeAnalysis.executiveSummary}
								strengths={activeAnalysis.strengths}
								improvements={activeAnalysis.improvements}
								allQuotes={activeAnalysis.notableQuotes}
								meetingTitle={meeting.title}
								meetingDate={new Date(meeting.meetingDate).toLocaleDateString("en-US", {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
								actionItems={data.meeting.metadata.actionItems ?? null}
								meName={sessionData?.user?.name ?? null}
							/>
						</div>
					) : (
						<div className="flex items-center justify-center overflow-y-auto">
							<EmptyState
								icon={Presentation}
								title="No analysis for this speaker"
								body="Analysis hasn't been run for this speaker yet."
								primary={
									activeSpeakerEntry ? (
										<Button
											size="sm"
											disabled={analyze.isPending || isAnalyzing}
											onClick={() => analyze.mutate(activeSpeakerEntry.meetingSpeaker.speakerId)}
										>
											{isAnalyzing ? "Working…" : "Analyze"}
										</Button>
									) : undefined
								}
							/>
						</div>
					)
				) : (
					<TranscriptPane
						lines={data.transcript}
						activeDim={dim ?? CATEGORY_KEYS[0]}
						activeSpeakerId={speakerId}
						speakerNames={speakerNames}
						durationSec={
							audioRec?.durationSec ??
							(meeting.durationMinutes != null ? meeting.durationMinutes * 60 : null)
						}
						audioRef={audioRef}
						onAnnotationClick={(d) => {
							setDim(d);
							setView("analyze");
						}}
						onShowAll={() => {
							// Reset to first dim to show all annotations
							const firstWithAnnotation = CATEGORY_KEYS.find((k) =>
								data.transcript.some((l) => l.annotations.some((a) => a.dimensionKey === k)),
							);
							if (firstWithAnnotation) setDim(firstWithAnnotation);
						}}
					/>
				)}
			</div>
		</>
	);
}

// Re-export so the existing route wiring continues to work
export default MeetingDetailPage;
